/**
 * NurseryOwnerSignupWizard
 * ---------------------------------------------------------------------------
 * Guided 4-step signup for childcare providers ("New Company" flow).
 *
 * Step 1 · Choose plan       — pricing cards inline; sets ?plan + ?interval
 * Step 2 · Your details       — owner name, email, password, phone
 * Step 3 · Your nursery       — org name, first centre, currency, timezone
 * Step 4 · Confirm & launch   — summary + Day-1 value checklist + submit
 *
 * On success the owner is auto-logged-in and routed to /admin.
 *
 * This flow is DISTINCT from `Login.js` — that page is now dedicated to
 * parent/staff sign-in. The single link "Are you a nursery owner? Start a
 * free trial" from Login drops the user here so intents don't collide.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../utils/api';
import { PLANS, formatPrice, annualSavings } from '../config/plans';
import { analytics, EVENTS } from '../utils/analytics';
import { toast } from 'sonner';

function slugify(s = '') {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40);
}

const STEPS = [
  { key: 'plan',    title: 'Choose your plan',    subtitle: 'Every plan starts with a 14-day free trial — no card needed.' },
  { key: 'owner',   title: 'Your details',        subtitle: 'This is the owner account. You can invite team-mates later.' },
  { key: 'nursery', title: 'Your nursery',        subtitle: 'A little about the setting so we can tailor the workspace.' },
  { key: 'confirm', title: 'Confirm & launch',    subtitle: 'One last look before we create your workspace.' },
];

function NurseryOwnerSignupWizard({ onLogin }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Restore the plan chosen on the pricing page (or fall back to Growth,
  // the "sweet spot" tier). Same for billing interval.
  const initialPlan = (searchParams.get('plan') || 'growth').toLowerCase();
  const initialInterval = (searchParams.get('interval') || 'monthly').toLowerCase();

  const [stepIdx, setStepIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  const [form, setForm] = useState({
    plan_key: PLANS.find((p) => p.key === initialPlan) ? initialPlan : 'growth',
    billing_interval: ['monthly', 'annual'].includes(initialInterval) ? initialInterval : 'monthly',
    owner_name: '',
    owner_email: '',
    owner_password: '',
    contact_phone: '',
    organization_name: '',
    center_name: '',
    center_address: '',
    center_city: '',
    center_postcode: '',
    center_capacity: '50',
    currency: 'GBP',
    timezone: 'Europe/London',
    org_type: 'single_center',
  });

  const step = STEPS[stepIdx];
  const selectedPlan = useMemo(
    () => PLANS.find((p) => p.key === form.plan_key) || PLANS[1],
    [form.plan_key],
  );

  // Keep the URL in sync so a mid-wizard refresh preserves the plan
  // choice (the rest of the form is intentionally memory-only for
  // simplicity — nothing sensitive should live in the URL).
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set('plan', form.plan_key);
    next.set('interval', form.billing_interval);
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.plan_key, form.billing_interval]);

  // Emit signup_started exactly once per wizard visit so the funnel
  // (landing_viewed → pricing_viewed → signup_started → signup_completed)
  // shows honest drop-off numbers. The `plan` and `interval` come along
  // for slicing in PostHog Insights.
  useEffect(() => {
    analytics.track(EVENTS.SIGNUP_STARTED, {
      plan: form.plan_key,
      interval: form.billing_interval,
      source: 'wizard',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validateCurrentStep = () => {
    if (step.key === 'plan') {
      if (!form.plan_key) return 'Pick a plan to continue.';
      return null;
    }
    if (step.key === 'owner') {
      if (!form.owner_name.trim()) return 'Please add your full name.';
      if (!form.owner_email.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.owner_email)) return 'Please add a valid email address.';
      if ((form.owner_password || '').length < 8) return 'Password must be at least 8 characters.';
      const digits = (form.contact_phone || '').replace(/\D/g, '');
      if (digits.length < 7) return 'Please add a contact phone number so we can reach you.';
      return null;
    }
    if (step.key === 'nursery') {
      if (!form.organization_name.trim()) return 'Please add your nursery / childcare setting name.';
      if (!form.center_name.trim()) return 'Please add a name for your first centre (this can just match the nursery name).';
      return null;
    }
    return null;
  };

  const handleNext = () => {
    const err = validateCurrentStep();
    if (err) {
      setFormError(err);
      // Nudge the error into view — helps on longer forms where the
      // Continue button lives below the fold on narrow viewports.
      setTimeout(() => {
        try {
          document.querySelector('[data-testid="wizard-error"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } catch (_) { /* SSR / older browsers */ }
      }, 30);
      return;
    }
    setFormError('');
    setStepIdx((i) => Math.min(i + 1, STEPS.length - 1));
  };

  const handleBack = () => {
    setFormError('');
    setStepIdx((i) => Math.max(i - 1, 0));
  };

  const handleSubmit = async () => {
    const err = validateCurrentStep();
    if (err) { setFormError(err); return; }
    setSubmitting(true);
    setFormError('');
    try {
      const slug = slugify(form.organization_name) || slugify(form.owner_email.split('@')[0]) || `nursery-${Date.now()}`;
      const res = await api.registerOrganization({
        organization_name: form.organization_name.trim(),
        slug,
        owner_email: form.owner_email.trim(),
        owner_password: form.owner_password,
        owner_name: form.owner_name.trim(),
        contact_phone: form.contact_phone.trim(),
        type: form.org_type,
        currency: form.currency,
        timezone: form.timezone,
        plan_key: form.plan_key,
        billing_interval: form.billing_interval,
        first_center: {
          center_id: 'tmp',
          name: form.center_name.trim() || form.organization_name.trim(),
          address: form.center_address.trim(),
          city: form.center_city.trim(),
          postcode: form.center_postcode.trim(),
          phone: form.contact_phone.trim(),
          email: form.owner_email.trim(),
          capacity: parseInt(form.center_capacity || '50', 10) || 50,
          age_groups: ['0-2', '2-3', '3-5'],
        },
      });
      localStorage.setItem('token', res.access_token);
      const me = await api.getMe();
      // Signup complete — this is a *conversion*. Identify first (so the
      // event lands on the new person profile) then fire the event so
      // the user is properly attributed in PostHog.
      analytics.identify(me);
      analytics.track(EVENTS.SIGNUP_COMPLETED, {
        plan: form.plan_key,
        interval: form.billing_interval,
        organization_id: me.organization_id,
        organization_slug: me.organization_slug,
      });
      toast.success(`Welcome to Kids WiseApp, ${(me.full_name || '').split(' ')[0] || 'friend'}!`, {
        description: `Your 14-day free trial has started — no card needed.`,
      });
      onLogin?.(me, res.access_token);
      navigate('/');
    } catch (e) {
      setFormError(e.message || 'Something went wrong — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="nursery-owner-signup"
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, hsl(190 60% 96%) 0%, hsl(210 65% 97%) 60%, hsl(220 55% 98%) 100%)',
        padding: '2rem 1rem',
      }}
    >
      <div style={{ maxWidth: 920, margin: '0 auto' }}>
        {/* Brand + step indicator */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', gap: '1rem', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: '0.75rem', color: 'hsl(200 40% 40%)', letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 700 }}>
              Kids WiseApp · for childcare providers
            </div>
            <h1 style={{ margin: '0.15rem 0 0 0', fontSize: '1.7rem', color: 'hsl(200 60% 20%)' }}>
              {step.title}
            </h1>
            <p style={{ margin: '0.15rem 0 0 0', color: 'hsl(200 25% 40%)', fontSize: '0.9rem' }}>
              {step.subtitle}
            </p>
          </div>
          <a
            href="/login"
            style={{ fontSize: '0.85rem', color: 'hsl(200 60% 40%)', textDecoration: 'none' }}
            data-testid="already-have-account-link"
          >
            Already have an account? Sign in →
          </a>
        </div>

        {/* Step pips */}
        <div
          data-testid="wizard-step-indicator"
          style={{ display: 'flex', gap: 6, marginBottom: '1.5rem', flexWrap: 'wrap' }}
        >
          {STEPS.map((s, i) => (
            <div
              key={s.key}
              data-testid={`wizard-pip-${s.key}`}
              data-current={i === stepIdx ? 'true' : 'false'}
              data-done={i < stepIdx ? 'true' : 'false'}
              style={{
                flex: 1,
                minWidth: 120,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '0.4rem 0.6rem',
                background: i < stepIdx ? 'hsl(140 55% 95%)' : i === stepIdx ? 'white' : 'hsl(210 20% 96%)',
                border: `1px solid ${i === stepIdx ? 'hsl(190 60% 55%)' : i < stepIdx ? 'hsl(140 50% 78%)' : 'hsl(210 20% 88%)'}`,
                borderRadius: 8,
                fontSize: '0.75rem',
                fontWeight: 600,
                color: i < stepIdx ? 'hsl(140 40% 25%)' : i === stepIdx ? 'hsl(200 60% 25%)' : 'hsl(210 15% 50%)',
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 20, height: 20, borderRadius: '50%',
                  background: i < stepIdx ? 'hsl(140 55% 45%)' : i === stepIdx ? 'hsl(190 65% 50%)' : 'hsl(210 20% 82%)',
                  color: 'white', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.7rem', fontWeight: 700, flexShrink: 0,
                }}
              >
                {i < stepIdx ? '✓' : i + 1}
              </span>
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.title}</span>
            </div>
          ))}
        </div>

        {/* Card content */}
        <div
          className="card"
          style={{
            background: 'white',
            borderRadius: 12,
            padding: '1.5rem 1.75rem',
            boxShadow: '0 4px 24px hsl(200 40% 90% / 0.4)',
            border: '1px solid hsl(210 30% 92%)',
          }}
          data-testid={`wizard-step-${step.key}`}
        >
          {step.key === 'plan' && (
            <PlanStep
              value={form.plan_key}
              interval={form.billing_interval}
              onChangePlan={(k) => setField('plan_key', k)}
              onChangeInterval={(i) => setField('billing_interval', i)}
            />
          )}
          {step.key === 'owner' && (
            <OwnerStep form={form} setField={setField} />
          )}
          {step.key === 'nursery' && (
            <NurseryStep form={form} setField={setField} />
          )}
          {step.key === 'confirm' && (
            <ConfirmStep form={form} selectedPlan={selectedPlan} />
          )}

          {formError && (
            <div
              className="alert alert-error"
              data-testid="wizard-error"
              style={{
                color: 'hsl(0 65% 30%)',
                background: 'hsl(0 75% 96%)',
                border: '1px solid hsl(0 60% 85%)',
                padding: '0.55rem 0.75rem',
                borderRadius: 8,
                marginTop: '1rem',
                fontSize: '0.85rem',
              }}
            >
              {formError}
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', marginTop: '1.5rem', flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={handleBack}
              disabled={stepIdx === 0 || submitting}
              data-testid="wizard-back"
              style={{ visibility: stepIdx === 0 ? 'hidden' : 'visible' }}
            >
              ← Back
            </button>
            {stepIdx < STEPS.length - 1 ? (
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleNext}
                disabled={submitting}
                data-testid="wizard-next"
                style={{ minWidth: 160 }}
              >
                Continue →
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSubmit}
                disabled={submitting}
                data-testid="wizard-submit"
                style={{ minWidth: 200 }}
              >
                {submitting ? 'Creating your nursery…' : '🚀 Create my nursery'}
              </button>
            )}
          </div>
        </div>

        {/* Reassurance footer */}
        <div style={{ textAlign: 'center', marginTop: '1.25rem', fontSize: '0.75rem', color: 'hsl(200 25% 45%)' }}>
          14-day free trial · No card needed · Cancel anytime
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Step 1 — Plan picker                                                       */
/* -------------------------------------------------------------------------- */
function PlanStep({ value, interval, onChangePlan, onChangeInterval }) {
  return (
    <div>
      {/* Monthly / annual toggle */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 4, marginBottom: '1.25rem' }} data-testid="billing-interval-toggle">
        {['monthly', 'annual'].map((iv) => (
          <button
            key={iv}
            type="button"
            onClick={() => onChangeInterval(iv)}
            className={interval === iv ? 'btn btn-primary' : 'btn btn-ghost'}
            style={{ fontSize: '0.8rem', padding: '0.3rem 0.9rem' }}
            data-testid={`billing-interval-${iv}`}
          >
            {iv === 'monthly' ? 'Monthly' : 'Annual · save 17%'}
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.85rem' }}>
        {PLANS.map((plan) => {
          const isSelected = value === plan.key;
          const priceStr = formatPrice(plan, interval);
          const savings = interval === 'annual' ? annualSavings(plan) : null;
          return (
            <label
              key={plan.key}
              data-testid={`plan-card-${plan.key}`}
              data-selected={isSelected ? 'true' : 'false'}
              style={{
                cursor: 'pointer',
                background: isSelected ? 'hsl(190 65% 97%)' : 'white',
                border: `2px solid ${isSelected ? 'hsl(190 65% 50%)' : plan.recommended ? 'hsl(150 55% 65%)' : 'hsl(210 25% 88%)'}`,
                borderRadius: 10,
                padding: '1rem 0.9rem',
                position: 'relative',
                transition: 'border-color 150ms ease, background 150ms ease',
              }}
            >
              <input
                type="radio"
                name="plan"
                value={plan.key}
                checked={isSelected}
                onChange={() => onChangePlan(plan.key)}
                style={{ position: 'absolute', top: '0.7rem', right: '0.7rem' }}
                aria-label={`Choose ${plan.name} plan`}
              />
              {plan.recommended && (
                <div
                  style={{
                    position: 'absolute',
                    top: -10,
                    left: '0.9rem',
                    background: 'hsl(150 55% 45%)',
                    color: 'white',
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    padding: '0.15rem 0.55rem',
                    borderRadius: 999,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Most popular
                </div>
              )}
              <div style={{ fontSize: '1.4rem', marginBottom: '0.15rem' }}>{plan.icon}</div>
              <div style={{ fontWeight: 700, fontSize: '1.05rem', color: 'hsl(200 60% 20%)' }}>{plan.name}</div>
              <div style={{ fontSize: '0.72rem', color: 'hsl(200 25% 40%)', minHeight: 32, marginTop: '0.15rem' }}>{plan.tagline}</div>
              <div style={{ marginTop: '0.55rem', fontSize: '1.3rem', fontWeight: 700, color: 'hsl(200 60% 22%)' }}>
                {priceStr}
                <span style={{ fontSize: '0.75rem', fontWeight: 400, color: 'hsl(200 25% 45%)' }}>
                  {' '}/ {interval === 'monthly' ? 'mo' : 'yr'}
                </span>
              </div>
              {savings && (
                <div style={{ fontSize: '0.7rem', color: 'hsl(140 55% 32%)', fontWeight: 600, marginTop: 2 }}>
                  {savings} vs monthly
                </div>
              )}
              <div style={{ marginTop: '0.6rem', fontSize: '0.65rem', color: 'hsl(200 25% 40%)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                {plan.trialDays > 0 ? `${plan.trialDays}-day free trial` : 'Custom contract'}
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: '0.6rem 0 0', fontSize: '0.78rem', color: 'hsl(210 20% 25%)' }}>
                {(plan.features || []).slice(0, 5).map((f) => (
                  <li key={f} style={{ marginBottom: '0.2rem' }}>
                    <span style={{ color: 'hsl(150 55% 45%)', fontWeight: 700, marginRight: 4 }}>✓</span> {f}
                  </li>
                ))}
              </ul>
            </label>
          );
        })}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Step 2 — Owner details                                                     */
/* -------------------------------------------------------------------------- */
function OwnerStep({ form, setField }) {
  return (
    <div style={{ display: 'grid', gap: '0.9rem', maxWidth: 500 }}>
      <TextField
        label="Full name"
        value={form.owner_name}
        onChange={(v) => setField('owner_name', v)}
        placeholder="e.g. Jane Smith"
        testId="owner-name"
        required
      />
      <TextField
        label="Email"
        type="email"
        value={form.owner_email}
        onChange={(v) => setField('owner_email', v)}
        placeholder="you@your-nursery.co.uk"
        testId="owner-email"
        required
      />
      <TextField
        label="Password"
        type="password"
        value={form.owner_password}
        onChange={(v) => setField('owner_password', v)}
        placeholder="At least 8 characters"
        testId="owner-password"
        required
      />
      <TextField
        label="Contact phone"
        type="tel"
        value={form.contact_phone}
        onChange={(v) => setField('contact_phone', v)}
        placeholder="07700 900123"
        testId="owner-phone"
        required
      />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Step 3 — Nursery details                                                   */
/* -------------------------------------------------------------------------- */
function NurseryStep({ form, setField }) {
  return (
    <div style={{ display: 'grid', gap: '0.9rem', maxWidth: 620 }}>
      <TextField
        label="Nursery / setting name"
        value={form.organization_name}
        onChange={(v) => {
          setField('organization_name', v);
          if (!form.center_name) setField('center_name', v);
        }}
        placeholder="e.g. Sunshine Nursery Ltd"
        testId="org-name"
        required
      />
      <div>
        <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'hsl(210 25% 30%)', display: 'block', marginBottom: 3 }}>
          Setting type
        </label>
        <select
          value={form.org_type}
          onChange={(e) => setField('org_type', e.target.value)}
          style={{ width: '100%', padding: '0.5rem 0.6rem', fontSize: '0.9rem', border: '1px solid hsl(210 25% 85%)', borderRadius: 6, background: 'white' }}
          data-testid="org-type"
        >
          <option value="single_center">Single setting (one location)</option>
          <option value="multi_center">Multi-site (multiple locations)</option>
          <option value="childminder">Childminder</option>
        </select>
      </div>

      <div style={{ borderTop: '1px dashed hsl(210 25% 85%)', paddingTop: '0.9rem' }}>
        <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'hsl(210 25% 25%)', marginBottom: '0.55rem' }}>Your first centre</div>
        <div style={{ display: 'grid', gap: '0.65rem', gridTemplateColumns: '1fr 1fr' }}>
          <TextField
            label="Centre name"
            value={form.center_name}
            onChange={(v) => setField('center_name', v)}
            placeholder="e.g. Main site"
            testId="center-name"
            required
          />
          <TextField
            label="Approx. capacity"
            type="number"
            value={form.center_capacity}
            onChange={(v) => setField('center_capacity', v)}
            placeholder="50"
            testId="center-capacity"
          />
          <TextField
            label="Address"
            value={form.center_address}
            onChange={(v) => setField('center_address', v)}
            placeholder="Street / building"
            testId="center-address"
          />
          <TextField
            label="City"
            value={form.center_city}
            onChange={(v) => setField('center_city', v)}
            placeholder="London"
            testId="center-city"
          />
          <TextField
            label="Postcode"
            value={form.center_postcode}
            onChange={(v) => setField('center_postcode', v)}
            placeholder="SW1A 1AA"
            testId="center-postcode"
          />
          <div>
            <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'hsl(210 25% 30%)', display: 'block', marginBottom: 3 }}>
              Currency
            </label>
            <select
              value={form.currency}
              onChange={(e) => setField('currency', e.target.value)}
              style={{ width: '100%', padding: '0.5rem 0.6rem', fontSize: '0.9rem', border: '1px solid hsl(210 25% 85%)', borderRadius: 6, background: 'white' }}
              data-testid="currency"
            >
              <option value="GBP">GBP (£)</option>
              <option value="EUR">EUR (€)</option>
              <option value="USD">USD ($)</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Step 4 — Confirm & Day-1 value                                             */
/* -------------------------------------------------------------------------- */
function ConfirmStep({ form, selectedPlan }) {
  return (
    <div style={{ display: 'grid', gap: '1rem' }}>
      <div style={{ background: 'hsl(190 65% 97%)', border: '1px solid hsl(190 60% 82%)', borderRadius: 10, padding: '0.9rem 1rem' }}>
        <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, color: 'hsl(200 55% 32%)', marginBottom: 4 }}>
          Summary
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.6rem 1rem', fontSize: '0.88rem' }}>
          <SummaryRow label="Nursery" value={form.organization_name} />
          <SummaryRow label="Owner" value={`${form.owner_name} (${form.owner_email})`} />
          <SummaryRow label="Plan" value={`${selectedPlan.icon} ${selectedPlan.name} · ${form.billing_interval}`} />
          <SummaryRow label="Trial" value={selectedPlan.trialDays > 0 ? `${selectedPlan.trialDays} days free — no card needed` : 'Custom contract'} />
          <SummaryRow label="Currency" value={form.currency} />
          <SummaryRow label="Type" value={form.org_type.replace('_', ' ')} />
        </div>
      </div>

      <div style={{ border: '1px solid hsl(210 30% 90%)', borderRadius: 10, padding: '0.9rem 1rem', background: 'white' }}>
        <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, color: 'hsl(150 55% 32%)', marginBottom: 8 }}>
          What you get on day 1
        </div>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: '0.4rem', fontSize: '0.85rem', color: 'hsl(210 20% 25%)' }}>
          <ValueLi>19 Ofsted-aligned policies pre-seeded & published to parents</ValueLi>
          <ValueLi>Custody-safe collection with QR check-in / check-out</ValueLi>
          <ValueLi>Parent app: bookings, moments, messages, invoices</ValueLi>
          <ValueLi>UK payroll: timesheets → PAYE / NI PDFs</ValueLi>
          <ValueLi>Auto-generated staff onboarding checklist</ValueLi>
          <ValueLi>Guided admin bookings with payment-choice guardrails</ValueLi>
        </ul>
      </div>

      <p style={{ fontSize: '0.75rem', color: 'hsl(210 20% 45%)', margin: 0 }}>
        By creating your nursery you agree to our <a href="/terms" target="_blank" rel="noreferrer">Terms of Service</a> and <a href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>. We&apos;ll never auto-charge you without asking — we&apos;ll email a reminder 3 days before your trial ends.
      </p>
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: '0.7rem', color: 'hsl(210 20% 45%)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>{label}</div>
      <div style={{ fontWeight: 600, color: 'hsl(210 25% 20%)' }}>{value || '—'}</div>
    </div>
  );
}

function ValueLi({ children }) {
  return (
    <li style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
      <span style={{ color: 'hsl(150 55% 40%)', fontWeight: 700 }}>✓</span>
      <span>{children}</span>
    </li>
  );
}

function TextField({ label, value, onChange, placeholder, type = 'text', testId, required }) {
  return (
    <div>
      <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'hsl(210 25% 30%)', display: 'block', marginBottom: 3 }}>
        {label}{required && <span style={{ color: 'hsl(0 65% 45%)', marginLeft: 3 }}>*</span>}
      </label>
      <input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        required={!!required}
        aria-required={required ? 'true' : 'false'}
        autoComplete={type === 'password' ? 'new-password' : type === 'email' ? 'email' : 'off'}
        style={{
          width: '100%',
          padding: '0.55rem 0.7rem',
          fontSize: '0.9rem',
          border: '1px solid hsl(210 25% 85%)',
          borderRadius: 6,
          background: 'white',
        }}
      />
    </div>
  );
}

export default NurseryOwnerSignupWizard;
